import { useCallback, useEffect, useState } from 'react';
import { SERVICES, PAYMENT_METHODS } from '@autopass-web/domain';
import { useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/fp/isEmpty';
import { ConfirmPaymentModule } from '@autopass-web/mochi-payment';
import { PATH } from '#/guest-aps/config';
import { environment } from '#/guest-aps/environments/environment';

import { ERROR_CODES } from '#/guest-aps/config';
import { useAmplitedeAuthPageview } from '#/guest-aps/hooks/use-amplitude-auth-pageview';

type ValueOf<T> = T[keyof T];

// linepay has no hashParams
type HashParams = {
  custom_data: null;
  merchant_transaction_id: string;
  message: string;
  result: 'success' | 'fail';
  source:
    | 'one_time_easy_wallet'
    | 'line_pay'
    | 'credit_card'
    | 'one_time_jkopay';
  title: string;
  // JKO & easy-wallet is optional
  token?: string;
};

type SearchParams = {
  createTransactionId: string;
  parkingTicketId: string;
  paymentType: ValueOf<typeof PAYMENT_METHODS>;
  plateNumber: string;
  serviceType: ValueOf<typeof SERVICES>;

  // only for line pay
  parking_ticket_id?: string;
  parking_order_id?: string;
  status?: 'success' | 'failed';
};

export type ParsedError = {
  title: string;
  description: string;
  backendErrorCode: string;
  statusCode: number;
  requestUrl: string;
  requestMethod: string;
  requestPayload: Record<string, unknown>;
};

export const AuthPage = () => {
  const [isInit, setIsInit] = useState<boolean>(false);
  const navigate = useNavigate();
  const url = new URL(window.location.href);
  const { hash, search } = url;
  const {
    custom_data: customData,
    merchant_transaction_id: merchantTransactionId,
    ...rest
  } = parseParams(hash) as HashParams;
  const searchParams = parseParams(search) as SearchParams;
  const eventsParams = {
    停車憑證id: searchParams.parkingTicketId,
    車牌號碼: searchParams.plateNumber,
  };

  const handleErrorRediect = useCallback(
    (error: Partial<ParsedError>) => {
      navigate(`${PATH.ERROR}?code=${ERROR_CODES.PAYMENT_ERROR}`, {
        replace: true,
        state: error,
      });
    },
    [navigate],
  );

  const handleSuccessRedirect = useCallback(
    (result: { data: Record<string, unknown> }) => {
      navigate(`${PATH.SUCCESS}`, {
        state: {
          paymentType: searchParams.paymentType,
          ...result,
        },
        replace: true,
      });
    },
    [navigate, searchParams],
  );

  useAmplitedeAuthPageview(
    {
      plateNumber: searchParams.plateNumber,
      ticketId: searchParams.parkingTicketId,
      paymentType: searchParams.paymentType,
    },
    isInit,
  );

  useEffect(() => {
    if (isInit) return;
    setIsInit(true);
  }, [isInit]);

  if (!isInit) return null;

  return (
    <ConfirmPaymentModule
      amplitudeKey={environment.amplitudeKey}
      amplitudeEventParams={eventsParams}
      confirmPaymentInfo={{
        merchantTransactionId,
        customData,
        ...rest,
        ...searchParams,
      }}
      onErrorRedirect={handleErrorRediect}
      onSuccessRedirect={handleSuccessRedirect}
    />
  );
};

function parseParams(params: string) {
  const parsedParams = new URLSearchParams(params.substring(1));
  const result: Record<string, unknown> = {};

  for (const [key, value] of parsedParams) {
    const currentValue = decodeURIComponent(value);
    if (isEmpty(currentValue)) continue;
    result[key] = parseStringToActualType(currentValue);
  }

  return result;
}

function parseStringToActualType(value: string) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}
